import LanguageMenu from '@components/LanguageMenu'
import { PenIcon } from '@icons/PenIcon'
import cn from 'classnames'
import { useSessionId } from 'mmfintech-checkout-commons'
import { GlobalContext, tr } from 'mmfintech-commons'
import type React from 'react'
import { useContext } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { ThemeContext } from 'styled-components'
import BlixcardLogo from '../../assets/images/blixcard-white.png'
import settings from '../../settings'
import { CheckoutVoucherBody, CheckoutVoucherHeader, CheckoutVoucherUpper } from '../Commons'
import { BlixcardAmount, BlixcardHeader, BlixcardHeaderBack } from './Blixcard.styled'
import styles from './CheckoutContainer.module.scss'

interface WithCheckoutContainerOptions {
  showLanguageMenu?: boolean
  showHeader?: boolean
}

export const withCheckoutContainer = <P extends object>(
  Component: React.FC<P>,
  options: WithCheckoutContainerOptions = {
    showLanguageMenu: false,
    showHeader: false
  }
): React.FC<P> => {
  return (props: P) => {
    const { modalShow } = useContext(GlobalContext)

    const themeContext = useContext(ThemeContext)

    const { session } = useSelector(
      ({ checkout: { session } }) => ({
        session
      }),
      shallowEqual
    )

    const { showLanguageMenu, showHeader } = options
    const { amount, currency, showAmount, showCurrency } = session || {}

    const sessionId = useSessionId()

    const displayAmount = showAmount || amount
    const displayCurrency = showCurrency || currency

    if (settings.blixcard) {
      return (
        <div className='checkout-inner-container'>
          <BlixcardHeaderBack />
          <BlixcardHeader>
            <img src={BlixcardLogo} alt='' />
            {showLanguageMenu && <LanguageMenu />}
          </BlixcardHeader>

          {showHeader && displayAmount > 0 && (
            <BlixcardAmount>
              <div>
                <span className='amount' data-test='amount'>
                  {displayAmount}
                </span>
                <span className='currency' data-test='currency'>
                  {displayCurrency}
                </span>
              </div>
            </BlixcardAmount>
          )}

          <CheckoutVoucherBody className={cn({ blixcard: showHeader && displayAmount > 0 })}>
            <Component {...(props as P)} />
          </CheckoutVoucherBody>

          {Boolean(sessionId) && (
            <div className={styles['checkout-footer']}>
              {typeof modalShow === 'function' && (
                <div
                  className={styles['footer-link']}
                  onClick={() => modalShow(null)}
                  data-test='report-a-problem-button'
                >
                  <PenIcon />
                  <span>{tr('CHECKOUT.PROBLEM.TITLE', 'Report a Problem')}</span>
                </div>
              )}

              {Boolean(session?.returnUrl) && (
                <a className={styles['footer-link']} href={session?.returnUrl} target='_top'>
                  &larr; {tr('CHECKOUT.PAYMENT.BACK_TO_MERCHANT', 'Back to merchant')}
                </a>
              )}
            </div>
          )}
        </div>
      )
    }

    return (
      <div className='checkout-inner-container'>
        <CheckoutVoucherUpper>
          {'brandLogoText' in themeContext ? (
            <b className='logo'>{themeContext.brandLogoText}</b>
          ) : (
            <img src={themeContext.brandLogo} alt='' />
          )}
          {showLanguageMenu && <LanguageMenu />}
        </CheckoutVoucherUpper>
        <div>
          {showHeader && (
            <CheckoutVoucherHeader>
              <span className='amount' data-test='amount'>
                {displayAmount}
              </span>
              <span className='currency' data-test='currency'>
                {displayCurrency}
              </span>
            </CheckoutVoucherHeader>
          )}
          <CheckoutVoucherBody>
            <Component {...(props as P)} />
          </CheckoutVoucherBody>
        </div>

        {Boolean(sessionId) && (
          <div className={styles['checkout-footer']}>
            {typeof modalShow === 'function' && (
              <div
                className={styles['footer-link']}
                onClick={() => modalShow(null)}
                data-test='report-a-problem-button'
              >
                <PenIcon />
                <span>{tr('CHECKOUT.PROBLEM.TITLE', 'Report a Problem')}</span>
              </div>
            )}

            {Boolean(session?.returnUrl) && (
              <a className={styles['footer-link']} href={session?.returnUrl} target='_top'>
                &larr; {tr('CHECKOUT.PAYMENT.BACK_TO_MERCHANT', 'Back to merchant')}
              </a>
            )}
          </div>
        )}
      </div>
    )
  }
}
